import { AxiosResponse } from 'axios'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

import { AuthFormShell } from '@/components/PageHeader'
import { AuthSubmitButton } from '@/components/auth/AuthSubmitButton'
import {
  authFieldClassName,
  authLabelClassName,
  authLinkClassName,
} from '@/components/auth/authStyles'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import API from '@/lib/api'
import { cn } from '@/lib/utils'

interface ForgotPasswordFormProps {
  action: string
  onSuccess?: (response: AxiosResponse) => void
}

interface ForgotPasswordFormValues {
  email: string
}

export const ForgotPasswordForm = ({
  action,
  onSuccess,
}: ForgotPasswordFormProps) => {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>()

  const onSubmit = (values: ForgotPasswordFormValues) => {
    if (isSubmitting) return
    if (Object.keys(errors).length > 0) return
    if (!values.email) return

    setIsSubmitting(true)

    API.post(action, {
      email: values.email,
    })
      .then((response) => {
        setIsSubmitting(false)
        if (response.status === 200 || response.status === 204) {
          return onSuccess && onSuccess(response)
        } else {
          toast.error(t('forgotPassword.errors.generic.title'), {
            description: t('forgotPassword.errors.generic.description'),
            duration: 5000,
            closeButton: true,
          })
        }
      })
      .catch((error) => {
        setIsSubmitting(false)

        toast.error(t('forgotPassword.errors.generic.title'), {
          description: t('forgotPassword.errors.generic.description'),
          duration: 5000,
          closeButton: true,
        })

        console.error(error)
        return error
      })
  }

  return (
    <AuthFormShell
      title={t('forgotPassword.title')}
      description={t('forgotPassword.description')}
    >
      <form action={action} method="POST" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="email" className={authLabelClassName}>
            {t('forgotPassword.fields.email.label')}
          </Label>
          <Input
            id="email"
            placeholder={t('forgotPassword.fields.email.placeholder')}
            disabled={isSubmitting}
            className={cn(authFieldClassName, errors.email && 'border-destructive')}
            {...register('email', {
              required: 'forgotPassword.fields.email.errors.required',
            })}
          />
          {errors.email && (
            <p className="text-sm text-destructive">{t(errors.email.message as string)}</p>
          )}
        </div>

        <AuthSubmitButton disabled={isSubmitting} type="submit">
          {isSubmitting ? t('forgotPassword.submitting') : t('forgotPassword.submit')}
        </AuthSubmitButton>

        <p className="text-center text-xs text-brand-secondary">
          <Link to="/auth/login" className={authLinkClassName}>
            {t('forgotPassword.backToLogin')}
          </Link>
        </p>
      </form>
    </AuthFormShell>
  )
}
